<template>
  <CModal
    :show.sync="isVisible"
    :close-on-backdrop="true"
    size="lg"
    centered
    @update:show="handleClose"
  >
    <template #header>
      <h5 class="modal-title">
        <CIcon name="cipInfo" size="sm" class="mr-2"/>
        <translate translate-context="accounting.ledgerExplanation">Order — Ledger Calculations</translate>
      </h5>
      <button type="button" class="close" @click="close">
        <span>&times;</span>
      </button>
    </template>

    <div class="ledger-explain">
      <!-- Intro / assumptions -->
      <div class="ledger-explain__intro">
        <div class="intro-row">
          <CIcon name="cipCoin" size="sm" class="mr-2 text-primary"/>
          <span>
            <translate translate-context="accounting.ledgerExplanation">All amounts are in the</translate>
            <strong class="ml-1"><translate translate-context="accounting.ledgerExplanation">order currency</translate></strong>.
          </span>
        </div>
        <div class="intro-row">
          <CIcon name="cipPercent" size="sm" class="mr-2 text-primary"/>
          <span>
            <translate translate-context="accounting.ledgerExplanation">VAT rate defaults to</translate>
            <strong class="ml-1">22% (IT)</strong>;
            <code>iDEAL</code> → <strong>21% (NL)</strong>,
            <code>Przelewy24</code> → <strong>8% (PL)</strong>.
          </span>
        </div>
        <div class="intro-row text-muted small">
          <CIcon name="cipInfo" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">
            Each line marked with → produces a ledger entry. The prefix (−) means the entry is negative.
          </translate>
        </div>
      </div>

      <!-- Section header -->
      <div class="section-title">
        <span class="section-title__badge">for each</span>
        <code class="section-title__target">OrderItem</code>
        <span class="section-title__note">
          <translate translate-context="accounting.ledgerExplanation">of a paid Order</translate>
        </span>
      </div>

      <!-- Percentages block -->
      <div class="formula-card formula-card--meta">
        <div class="formula-card__title">
          <CIcon name="cipPercent" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Percentages (from pica_service)</translate>
        </div>
        <ul class="formula-list">
          <li>
            <code>pica_fee_percentage</code>
            <span class="eq">=</span>
            <code class="src">pica_service.pica_fee_perc</code>
            <span class="note">
              <translate translate-context="accounting.ledgerExplanation">during-stay override if applicable</translate>
            </span>
          </li>
          <li>
            <code>organizer_fee_percentage</code>
            <span class="eq">=</span>
            <code class="src">pica_service.additional_fee_perc</code>
          </li>
          <li>
            <code>agent_percentage</code>
            <span class="eq">=</span>
            <code class="src">pica_service.agent_percent</code>
          </li>
        </ul>
      </div>

      <!-- Formulas -->
      <FormulaBlock
        name="pica_fee"
        formula="order_item.price × pica_fee_percentage / 100"
        :produces="[{ stakeholder: 'pica', entryType: 'REVENUE' }]"
      />

      <FormulaBlock
        name="organizer_fee"
        formula="order_item.price × organizer_fee_percentage / 100"
        :produces="[{ stakeholder: 'organizer', entryType: 'REVENUE' }]"
        condition-context="accounting.ledgerExplanation"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">only if an ORGANIZER stakeholder is configured</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="vat"
        formula="order_item.price × vat_rate / (100 + vat_rate)"
        :produces="[{ stakeholder: 'pica', entryType: 'VAT', negative: true }]"
      />

      <FormulaBlock
        name="photographer_revenue"
        formula="order_item.price − pica_fee − organizer_fee − vat"
        :produces="[{ stakeholder: 'photographer', entryType: 'REVENUE' }]"
      />

      <FormulaBlock
        name="agent_fee"
        formula="pica_fee × agent_percentage / 100"
        :produces="[
          { stakeholder: 'agent', entryType: 'REVENUE' },
          { stakeholder: 'pica', entryType: 'REVENUE', negative: true }
        ]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">only if an agent is configured</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="stripe_fee"
        formula="order_item.payment_transaction_fee"
        :no-entry="true"
      />

      <FormulaBlock
        name="photographer_stripe"
        formula="stripe_fee × (100 − pica_fee_percentage + organizer_fee_percentage) / 100"
        :produces="[{ stakeholder: 'photographer', entryType: 'TRANSACTION_COST', negative: true }]"
      >
        <template #condition>
          <span>
            <translate translate-context="accounting.ledgerExplanation">0 if</translate>
            <code class="mx-1">pica_service.assigns_transaction_costs_to_pica = True</code>.
            <translate translate-context="accounting.ledgerExplanation">Entry is skipped if the result is 0.</translate>
          </span>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="pica_stripe"
        formula="stripe_fee − photographer_stripe"
        :produces="[{ stakeholder: 'pica', entryType: 'TRANSACTION_COST', negative: true }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">absorbs rounding on the Stripe split</translate>
        </template>
      </FormulaBlock>

      <FormulaBlock
        name="handling_fee"
        formula="order.handling_fee"
        :produces="[{ stakeholder: 'pica', entryType: 'HANDLING_FEE' }]"
      >
        <template #condition>
          <translate translate-context="accounting.ledgerExplanation">only if > 0; emitted on a separate ledger line</translate>
        </template>
      </FormulaBlock>

      <!-- Notes -->
      <div class="notes">
        <div class="notes__title">
          <CIcon name="cipInfo" size="sm" class="mr-2"/>
          <translate translate-context="accounting.ledgerExplanation">Notes</translate>
        </div>
        <ul class="notes__list">
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">EUR amounts</translate></strong>:
            <code>amount_eur = amount × order.payment_exchange_rate</code>.
            <translate translate-context="accounting.ledgerExplanation">
              payment_transaction_fee is natively in EUR — divide by the exchange rate when expressed in the order currency.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">VAT rate</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">
              the rate effectively applied on each entry (Stripe tax breakdown when available, else payment-method override, else IT 22%).
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Rounding</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">
              to 2 decimals, half-up. The only split producing a cent-level residue is the Stripe fee split between photographer and Pica — the residue is absorbed on pica_stripe. All other movements are balanced pairs or single entries.
            </translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Agent special case</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">agent id</translate>
            <code>337630</code>
            (Bonanni) →
            <code>agent_fee = photographer_revenue × agent_percentage / 100</code>
            <translate translate-context="accounting.ledgerExplanation">instead of the default formula.</translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Handling fee</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">does not currently emit its own VAT entry (tracked gap).</translate>
          </li>
          <li>
            <strong><translate translate-context="accounting.ledgerExplanation">Immutability</translate></strong>:
            <translate translate-context="accounting.ledgerExplanation">
              ledger rows are never updated. A DB unique constraint on (transaction_type, source_id) prevents the same order from being booked twice.
            </translate>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <CButton color="secondary" variant="outline" @click="close">
        <translate translate-context="accounting.ledgerExplanation">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import FormulaBlock from './FormulaBlock.vue'

export default {
  name: 'OrderLedgerExplanationModal',
  components: {FormulaBlock},
  data() {
    return {
      isVisible: false
    }
  },
  methods: {
    open() {
      this.isVisible = true
    },
    close() {
      this.isVisible = false
    },
    handleClose(val) {
      if (!val) this.close()
    }
  }
}
</script>

<style scoped>
.ledger-explain {
  padding: 4px 8px 16px;
}

.ledger-explain__intro {
  background: #f7f8fb;
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 18px;
}

.intro-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  line-height: 1.4;
}

.intro-row:last-child {
  margin-bottom: 0;
}

.intro-row code {
  background: #eef1f5;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.82rem;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 22px 0 10px;
  gap: 8px;
}

.section-title__badge {
  background: #100F23;
  color: #fff;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 3px;
  font-weight: 600;
}

.section-title__target {
  background: #eef1f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
}

.section-title__note {
  color: #6c7285;
  font-size: 0.9rem;
}

.formula-card--meta {
  background: #fafbfd;
  border: 1px dashed #c8cdd6;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
}

.formula-card__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 8px;
  color: #100F23;
}

.formula-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.formula-list li {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
  font-size: 0.9rem;
}

.formula-list code {
  background: #fff;
  border: 1px solid #e4e7ea;
  padding: 1px 6px;
  border-radius: 3px;
}

.formula-list .src {
  background: #eef1f5;
  border: none;
}

.formula-list .eq {
  color: #6c7285;
  font-weight: 600;
}

.formula-list .note {
  color: #6c7285;
  font-size: 0.78rem;
  font-style: italic;
}

.notes {
  margin-top: 22px;
  background: #fffaf0;
  border: 1px solid #f3dfb8;
  border-radius: 6px;
  padding: 12px 14px;
}

.notes__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #7a5a12;
  margin-bottom: 8px;
}

.notes__list {
  margin: 0;
  padding-left: 18px;
  color: #3c3a2a;
}

.notes__list li {
  margin-bottom: 6px;
  line-height: 1.45;
  font-size: 0.88rem;
}

.notes__list li:last-child {
  margin-bottom: 0;
}

.notes__list code {
  background: #fdf3d7;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.82rem;
}
</style>
